import React, { useMemo, useState } from "react";

import { Box, IconButton, MenuItem } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  ColumnFilter,
  GeneralActions,
  SearchSuggest,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";
import { filter } from "Util/filter";
import { getValueOrDefault } from "Util/shipeezi.table";

import { Color } from "../../../../../../../../components/Form/Button";
import { SplitButton } from "../../../../../../../../components/Form/SplitButton";
import { Fleet } from "../../../../../../../../models/fleet";
import { SearchAttribute } from "../../../../../../../../models/search.attribute";
import { Filters } from "../../../../../../../../models/shipeezi.table";
import { EquipmentOwnership, EquipmentType, Status } from "../../../../../../../../util/enums";
import { filterSearch } from "../../../../../../../../util/filterSearch";
import { onSearch } from "../../../../../../../../util/onSearch";
import { paginate } from "../../../../../../../../util/paginate";
import { useSubcontractorContext } from "../context/Subcontractor";
import AddEquipmentModal from "./AddEquipmentModal";
import useStyles from "./styles";

type Props = {
  isView: boolean;
};
const EquipmentsComponent: React.FC<Props> = ({ isView }) => {
  const { subcontractor, setSubcontractor, onUpdateSubcontractor } = useSubcontractorContext();

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [filters, setFilters] = useState<Filters>();
  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const [isAddEquipmentModalOpened, setIsAddEquipmentModalOpened] = useState(false);

  const equipmentsAdded = useMemo(() => {
    return subcontractor?.fleets || [];
  }, [subcontractor]);

  const equipmentsRenderable = useMemo(() => {
    let data = equipmentsAdded || [];

    if (searchAttributes.length > 0) data = filterSearch(data, searchAttributes);

    if (filters) data = filter(data, filters);

    return searchAttributes.length > 0 ? data : paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [equipmentsAdded, page, pageSize, searchAttributes, filters]);

  const onAddEquipmentsSelected = (fleets: Fleet[]) => {
    const map: Map<number, Fleet> = new Map();

    fleets.forEach((fleet) => {
      map.set(fleet.id!, fleet);
    });

    onUpdateSubcontractor("fleets", Array.from(map.values()));
  };

  const onRemoveEquipmentSelected = (fleetId: Fleet["id"]) => {
    setSubcontractor((prev) => ({
      ...(prev || {}),
      fleets: (prev?.fleets || []).filter((fleet) => Number(fleet.id) !== Number(fleetId)),
    }));
  };

  const columns: Column[] = [
    {
      name: "equipmentType",
      title: translate("type"),
      formatText: (value: Fleet["equipmentType"]) => value ?? "-",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "description",
      title: translate("description"),
      formatText: (value: Fleet["description"]) => value ?? "-",
    },
    {
      name: "fleetNumber",
      title: translate("fleetNumber"),
      formatText: (value: Fleet["fleetNumber"]) => value ?? "-",
    },
    {
      name: "ownership",
      title: translate("belongsTo"),
      hideSearch: true,
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "condition",
      title: translate("condition"),
      formatText: (value: Fleet["condition"]) => value ?? "-",
    },
    {
      name: "status",
      title: "Status",
      hideSearch: true,
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: "description", title: translate("description") },
          { name: "ownership", title: translate("belongsTo") },
          { name: "condition", title: translate("condition") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder={translate("search")}
      />
    );
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: ["newRecord"],
    },
    searchSuggest: renderSearchSuggest(),
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "equipmentType",
          options: [EquipmentType.FLEET, EquipmentType.GENERAL],
        },
        {
          name: translate("belongsTo"),
          column: "ownership",
          options: [
            EquipmentOwnership.OWN,
            EquipmentOwnership.RENTAL,
            EquipmentOwnership.SUBCONTRACTOR,
            EquipmentOwnership.LEASED,
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: isView ? null : (
      <SplitButton
        customColor={Color.SECONDARY_DARK}
        onClick={() => setIsAddEquipmentModalOpened(true)}
        options={
          <MenuItem onClick={() => setIsAddEquipmentModalOpened(true)}>
            {translate("addAnExistentEquipment")}
          </MenuItem>
        }
      >
        {translate("addEquipments")}
      </SplitButton>
    ),
  };

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  return (
    <Box className={classes.main}>
      <div data-cy="equipmentsListTable">
        <ShipeeziNewGrid
          data={equipmentsRenderable}
          columns={columns}
          totalItems={equipmentsAdded.length}
          loadingData={false}
          emptyData={translate("noEquipmentToShow")}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          currentPage={page}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          showSort={false}
          positionActionsColumn="right"
          generalActions={generalActions}
          rowActionsExtra={[
            {
              title: "remove",
              buttonAndAction: (fleetId) =>
                isView ? null : (
                  <IconButton onClick={() => onRemoveEquipmentSelected(fleetId)}>
                    <IconCloseCircle width={20} height={20} color={colors.error500} />
                  </IconButton>
                ),
            },
          ]}
        />
        <AddEquipmentModal
          isOpened={isAddEquipmentModalOpened}
          onClose={() => setIsAddEquipmentModalOpened(false)}
          equipmentsIdAdded={equipmentsAdded.map((fleet) => fleet.id as unknown as string)}
          onConfirmAddEquipments={onAddEquipmentsSelected}
        />
        {/* {openQuickCreation && (
          <QuickCreationModal setOpen={setOpenQuickCreation} open={openQuickCreation} />
        )} */}
      </div>
    </Box>
  );
};

export default EquipmentsComponent;
